import type { ReactNode } from 'react';
import { ContentLoader } from '@rio-cloud/rio-uikit';
import { useIntl } from 'react-intl';

import type { AegRequestDetails } from '../../../../hooks/useAegDetails';
import { formatAegAssignTo, type StageKey } from '../../types';

const VEHICLE_LABELS = [
    'support.aeg.newRequest.vehicleData.owner',
    'support.aeg.newRequest.vehicleData.model',
    'support.aeg.newRequest.vehicleData.chassis',
    'support.aeg.newRequest.vehicleData.licensePlate',
    'support.aeg.newRequest.vehicleData.vehicleType',
    'support.aeg.newRequest.vehicleData.color',
    'support.aeg.newRequest.vehicleData.implement',
    'support.aeg.newRequest.vehicleData.productionDate',
    'support.aeg.newRequest.vehicleData.billingDate',
    'support.aeg.newRequest.vehicleData.saleDate',
    'support.aeg.newRequest.vehicleData.tsDeliveryDate',
    'support.aeg.newRequest.vehicleData.responsibleDealer',
    'support.aeg.newRequest.vehicleData.warrantyZeroEnd',
    'support.aeg.newRequest.vehicleData.additionalWarrantyEnd',
    'support.aeg.newRequest.vehicleData.maintenanceContract',
    'support.aeg.newRequest.vehicleData.specialWarranty',
    'support.aeg.newRequest.vehicleData.pendingCampaigns',
    'support.aeg.newRequest.vehicleData.checkLegal',
    'support.aeg.newRequest.vehicleData.blockedVehicle',
    'support.aeg.newRequest.vehicleData.warranty',
    'support.aeg.newRequest.vehicleData.volksConfiaWarranty',
    'support.aeg.newRequest.vehicleData.lastUpdate',
];

const ValueLoader = ({ block = false }: { block?: boolean }) => (
    <ContentLoader className={block ? 'width-100pct' : 'max-width-100'} height={block ? 48 : 16} />
);

export default function Summary({
    details,
    loading,
    error,
    stage,
    assignTo,
}: {
    details: AegRequestDetails | null;
    loading: boolean;
    error: boolean;
    stage?: StageKey;
    assignTo?: string;
}) {
    const intl = useIntl();
    const label = (id: string, values?: Record<string, string | number>) =>
        intl.formatMessage({ id: `intl-msg:${id}` }, values);

    if (error || (!loading && !details)) {
        return (
            <div className='aeg-request-detail-empty padding-30 text-center text-color-dark'>
                {label('support.aeg.detail.summary.error')}
            </div>
        );
    }

    const aegNumber = details?.aegNumber ? (
        details?.aegNumber
    ) : stage === 'abertura' ? (
        <i className='text-color-dark'>{label('support.aeg.detail.summary.aegNumberPending')}</i>
    ) : (
        '—'
    );
    const vehicleFields = details?.vehicle ?? VEHICLE_LABELS.map(item => ({ label: item, value: '' }));

    return (
        <div className='aeg-request-detail-layout display-grid gap-10'>
            <div className='display-flex flex-column gap-10'>
                <DetailSection title={label('support.aeg.detail.summary.section.situation')}>
                    <div className='display-grid grid-cols-1 grid-cols-3-sm gap-10'>
                        <SummaryCard
                            label={label('support.aeg.detail.summary.repairValue')}
                            loading={loading}
                            value={details?.repairValue}
                        />
                        <SummaryCard
                            label={label('support.aeg.detail.summary.sinceOpening')}
                            loading={loading}
                            value={details?.daysSinceOpening}
                        />
                        <SummaryCard
                            label={label('support.aeg.newRequest.field.approvalType')}
                            loading={loading}
                            value={details?.approvalType}
                        />
                    </div>
                </DetailSection>
                <DetailSection title={label('support.aeg.detail.summary.section.information')}>
                    <dl className='display-grid grid-cols-1 grid-cols-4-sm gap-20 margin-0'>
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.newRequest.field.requestType')}
                            value={details?.requestType}
                        />
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.detail.summary.createdBy')}
                            value={details?.createdBy}
                        />
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.detail.summary.createdAt')}
                            value={details?.createdAt}
                        />
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.newRequest.vehicleData.lastUpdate')}
                            value={details?.updatedAt}
                        />
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.detail.summary.assignedTo')}
                            value={assignTo ? formatAegAssignTo(intl, assignTo) : details?.assignToLabel}
                        />
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.newRequest.field.serviceType')}
                            value={details?.serviceType}
                        />
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.detail.summary.warrantyType')}
                            value={details?.warrantyType}
                        />
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.detail.summary.stageTime')}
                            value={details?.stageTime}
                        />
                    </dl>
                </DetailSection>
                <DetailSection title={label('support.aeg.detail.summary.section.link')}>
                    <div className='display-flex align-items-center gap-10 padding-10 bg-lighter border rounded'>
                        <span className='rioglyph rioglyph-truck text-size-20 text-color-highlight-dark' />
                        <div className='flex-1-1'>
                            <strong className='display-block text-color-darkest'>
                                {loading ? <ValueLoader /> : details?.model}
                            </strong>
                            <span className='aeg-request-detail-mono text-size-12 text-color-dark'>
                                {loading ? (
                                    <ValueLoader />
                                ) : (
                                    label('support.aeg.detail.summary.chassis', { chassis: details?.chassis ?? '—' })
                                )}
                            </span>
                        </div>
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.detail.summary.os')}
                            value={mono(details?.osNumber)}
                        />
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.detail.summary.occurrence')}
                            value={details?.occurrenceNumber}
                        />
                    </div>
                </DetailSection>
                <DetailSection title={label('support.aeg.detail.summary.section.requester')}>
                    <dl className='display-grid grid-cols-1 grid-cols-4-sm gap-20 margin-0'>
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.detail.history.field.aegNumber')}
                            value={aegNumber}
                        />
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.newRequest.field.approvalType')}
                            value={details?.approvalType}
                        />
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.newRequest.field.dn')}
                            value={details?.dealer}
                        />
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.newRequest.field.regional')}
                            value={details?.regional}
                        />
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.newRequest.field.responsible')}
                            value={details?.responsible}
                        />
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.newRequest.field.phone')}
                            value={details?.phone}
                        />
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.detail.summary.openingDate')}
                            value={details?.openingDate}
                        />
                    </dl>
                </DetailSection>
                <DetailSection title={label('support.aeg.detail.summary.section.repair')}>
                    <dl className='display-grid grid-cols-1 grid-cols-4-sm gap-20 margin-0'>
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.newRequest.field.serviceOrder')}
                            value={mono(details?.osNumber)}
                        />
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.newRequest.field.warrantyRequest')}
                            value={mono(details?.sgNumber)}
                        />
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.newRequest.field.serviceType')}
                            value={details?.serviceType}
                        />
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.detail.summary.repairTotal')}
                            value={details?.repairValue}
                        />
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.detail.summary.labor')}
                            value={details?.labor}
                        />
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.newRequest.field.serviceCondition')}
                            value={details?.attendance}
                        />
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.detail.summary.hasThirdParty')}
                            value={details?.hasThirdParty}
                        />
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.detail.summary.invoiceValue')}
                            value={details?.invoiceValue}
                        />
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.detail.summary.thirdPartyDescription')}
                            value={details?.thirdPartyServices}
                        />
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.detail.summary.invoiceNumbers')}
                            value={details?.invoiceNumbers}
                        />
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.newRequest.field.repairScope')}
                            value={details?.repairScope}
                        />
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.newRequest.field.repairDate')}
                            value={details?.repairDate}
                        />
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.newRequest.field.odometer')}
                            value={details?.km}
                        />
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.newRequest.field.controlCode')}
                            value={details?.controlCode}
                        />
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.newRequest.field.chameVolks')}
                            value={mono(details?.chameVolks)}
                        />
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.newRequest.field.breakdown')}
                            value={details?.breakdown}
                        />
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.newRequest.field.fleetComposition')}
                            value={details?.fleetComposition}
                        />
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.newRequest.field.lastPurchase')}
                            value={details?.lastPurchase}
                        />
                        <DetailItem
                            loading={loading}
                            label={label('support.aeg.newRequest.field.companies')}
                            value={details?.companies}
                        />
                    </dl>
                    <div className='display-flex flex-column gap-10 margin-top-20'>
                        <DetailText
                            loading={loading}
                            label={label('support.aeg.newRequest.field.customerComplaint')}
                            value={details?.customerComplaint}
                        />
                        <DetailText
                            loading={loading}
                            label={label('support.aeg.newRequest.field.dnDiagnosis')}
                            value={details?.dnDiagnosis}
                        />
                        <DetailText
                            loading={loading}
                            label={label('support.aeg.newRequest.field.dnJustification')}
                            value={details?.dnJustification}
                        />
                    </div>
                </DetailSection>
            </div>
            <div className='aeg-request-detail-vehicle-panel display-flex flex-column gap-10'>
                <DetailSection title={label('support.aeg.newRequest.vehicleData.title')}>
                    <dl className='aeg-request-detail-vehicle-list margin-0'>
                        {vehicleFields.map(field => (
                            <div className='display-flex justify-content-between gap-10' key={field.label}>
                                <dt className='text-color-dark'>{label(field.label)}</dt>
                                <dd className='margin-0 text-right text-color-darkest'>
                                    {loading ? <ValueLoader /> : field.value}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </DetailSection>
                <DetailSection title={label('support.aeg.detail.summary.section.history')}>
                    <div className='aeg-request-detail-empty padding-30 text-center text-color-dark'>
                        {loading ? <ValueLoader /> : label('support.aeg.detail.summary.emptyHistory')}
                    </div>
                </DetailSection>
            </div>
        </div>
    );
}

function DetailSection({
    title,
    className = '',
    children,
}: {
    title: string;
    className?: string;
    children: ReactNode;
}) {
    return (
        <section className={`panel panel-default padding-15 margin-0 bg-white ${className}`}>
            <h3 className='aeg-request-detail-section-title margin-0 margin-bottom-15 text-bold text-color-darkest'>
                {title}
            </h3>
            {children}
        </section>
    );
}

function DetailItem({ label, value, loading = false }: { label: string; value?: ReactNode; loading?: boolean }) {
    return (
        <div>
            <dt className='text-size-12 text-medium text-color-dark'>{label}</dt>
            <dd className='margin-0 margin-top-5 text-color-darkest'>{loading ? <ValueLoader /> : value}</dd>
        </div>
    );
}

function DetailText({ label, value, loading = false }: { label: string; value?: string; loading?: boolean }) {
    return (
        <div>
            <span className='display-block margin-bottom-5 text-size-12 text-medium text-color-dark'>{label}</span>
            <div className='padding-10 bg-lightest border rounded text-color-darkest'>
                {loading ? <ValueLoader /> : value}
            </div>
        </div>
    );
}

function SummaryCard({ label, value, loading = false }: { label: string; value?: string; loading?: boolean }) {
    return (
        <div className='padding-15 bg-lightest border rounded'>
            {loading ? (
                <ValueLoader block />
            ) : (
                <strong className='aeg-request-detail-summary-value display-block text-color-darkest'>{value}</strong>
            )}
            <span className='display-block margin-top-5 text-size-12 text-color-dark'>{label}</span>
        </div>
    );
}

const mono = (value?: string) =>
    !value || value === '—' ? (value ?? '—') : <span className='aeg-request-detail-mono'>{value}</span>;
